import {
  CASE_PRIORITIES,
  CASE_SEVERITIES,
  CASE_STATUSES,
  createTestCaseSchema,
  type CasePriority,
  type CaseSeverity,
  type CaseStatus,
} from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { RichTextEditor } from '../../components/rich-text';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useT } from '../../i18n';
import { CaseAttributeSelect } from '../case-attributes/CaseAttributeSelect';
import { flattenFolders, useFolderTree } from '../folders/api';
import { EnvironmentSelect } from '../environments/EnvironmentSelect';
import { LabelPicker } from '../labels/LabelPicker';
import { StepsEditor, type StepValue } from './StepsEditor';
import { useCreateTestCase, useTestCase, useUpdateTestCase } from './api';

const detailsSchema = createTestCaseSchema.omit({ steps: true, labelIds: true });

interface DetailsValues {
  title: string;
  description?: string | null;
  preconditions?: string | null;
  testData?: string | null;
  priority: CasePriority;
  severity: CaseSeverity;
  status: CaseStatus;
  isAutomated: boolean;
  /** Empty only until a folder is chosen; a test case always has one. */
  folderId: string;
  /** `null` when the case is not tied to a particular stand. */
  environmentId?: string | null;
  /** `null` until the case is classified. */
  layerId?: string | null;
  moduleId?: string | null;
}

export interface TestCaseFormPageProps {
  projectCode: string;
  /** Present when editing an existing case. */
  caseKey?: string;
  /** Folder to start from, when the list already has one selected. */
  defaultFolderId?: string | undefined;
}

export const TestCaseFormPage = ({
  projectCode,
  caseKey,
  defaultFolderId,
}: TestCaseFormPageProps) => {
  const navigate = useNavigate();
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const { priority: priorityLabel, severity: severityLabel, caseStatus, automation } = useEnumLabels();
  const isEdit = caseKey !== undefined;

  const existing = useTestCase(projectCode, caseKey ?? '');
  const { data: folders } = useFolderTree(projectCode, 'case');
  const create = useCreateTestCase(projectCode);
  const update = useUpdateTestCase(projectCode, existing.data?.id ?? '');

  const [steps, setSteps] = useState<StepValue[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      title: '',
      description: '',
      preconditions: '',
      testData: '',
      priority: 'medium',
      severity: 'minor',
      status: 'active',
      isAutomated: false,
      folderId: defaultFolderId ?? '',
      environmentId: null,
      layerId: null,
      moduleId: null,
    },
  });

  useEffect(() => {
    if (!isEdit || !existing.data) return;
    const testCase = existing.data;
    reset({
      title: testCase.title,
      description: testCase.description ?? '',
      preconditions: testCase.preconditions ?? '',
      testData: testCase.testData ?? '',
      priority: testCase.priority,
      severity: testCase.severity,
      status: testCase.status,
      isAutomated: testCase.isAutomated,
      folderId: testCase.folderId ?? '',
      environmentId: testCase.environment?.id ?? null,
      layerId: testCase.layer?.id ?? null,
      moduleId: testCase.module?.id ?? null,
    });
    setSteps(
      testCase.steps.map((step) => ({
        action: step.action,
        expectedResult: step.expectedResult ?? '',
        screenshot: step.screenshot,
      })),
    );
    setLabelIds(testCase.labels.map((label) => label.id));
  }, [isEdit, existing.data, reset]);

  const cancel = async (): Promise<void> => {
    if (isEdit && caseKey) {
      await navigate({
        to: '/projects/$projectCode/test-cases/$caseKey',
        params: { projectCode, caseKey },
      });
      return;
    }
    await navigate({ to: '/projects/$projectCode/test-cases', params: { projectCode } });
  };

  const onSubmit = handleSubmit(async (values) => {
    // Drop half-written steps rather than failing validation on them.
    const payloadSteps = steps
      .filter((step) => step.action.trim().length > 0)
      .map((step) => ({
        action: step.action,
        expectedResult: step.expectedResult?.trim() ? step.expectedResult : null,
        screenshotId: step.screenshot?.id ?? null,
      }));

    const payload = {
      ...values,
      description: values.description?.trim() ? values.description : null,
      preconditions: values.preconditions?.trim() ? values.preconditions : null,
      testData: values.testData?.trim() ? values.testData : null,
      labelIds,
      steps: payloadSteps,
    };

    try {
      if (isEdit) {
        const saved = await update.mutateAsync(payload);
        toast.success(t('cases.saved', { key: saved.caseKey }));
        await navigate({
          to: '/projects/$projectCode/test-cases/$caseKey',
          params: { projectCode, caseKey: saved.caseKey },
        });
      } else {
        const saved = await create.mutateAsync(payload);
        toast.success(t('cases.created', { key: saved.caseKey }));
        await navigate({
          to: '/projects/$projectCode/test-cases/$caseKey',
          params: { projectCode, caseKey: saved.caseKey },
        });
      }
    } catch (error) {
      if (
        !applyApiError(error, setError, [
          'title',
          'folderId',
          'environmentId',
          'layerId',
          'moduleId',
          'description',
          'preconditions',
          'testData',
        ])
      ) {
        toast.error(errorText(error, 'cases.saveFailed'));
      }
    }
  });

  if (isEdit && existing.error) {
    return <ErrorState error={existing.error} onRetry={() => void existing.refetch()} />;
  }
  if (isEdit && existing.isPending) return <SkeletonRows rows={5} className="p-0" />;

  const folderOptions = flattenFolders(folders ?? []);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      <PageHeader
        title={isEdit ? t('cases.editTitle', { key: caseKey }) : t('cases.newTitle')}
        actions={
          <>
            {/* A full button, not a quiet link: leaving a half-written case is
                a real choice and has to be as easy to find as saving. It goes
                back where the editing started — the case itself, or the list
                when the case does not exist yet. */}
            <Button variant="secondary" onClick={() => void cancel()}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              {isEdit ? t('common.saveChanges') : t('cases.create')}
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader title={t('cases.basicInformation')} />
        <CardBody className="flex flex-col gap-4">
          <Field label={t('common.title')} error={errors.title?.message} required>
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                invalid={invalid}
                aria-describedby={describedBy}
                autoFocus
                placeholder={t('cases.titlePlaceholder')}
                {...register('title')}
              />
            )}
          </Field>

          <div className="grid gap-4 lg:grid-cols-3">
            <Field label={t('common.folder')} error={errors.folderId?.message} required>
              {({ id, invalid, describedBy }) => (
                <Controller
                  control={control}
                  name="folderId"
                  render={({ field }) => (
                    <SelectMenu
                      // See EnvironmentSelect: a value restored after mount needs a
                      // remount for the dropdown to show its name.
                      key={field.value || 'empty'}
                      id={id}
                      invalid={invalid}
                      aria-describedby={describedBy}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={t('cases.folderPlaceholder')}
                    >
                      {folderOptions.map((option) => (
                        <SelectMenuItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  )}
                />
              )}
            </Field>
            <Field label={t('common.environment')} error={errors.environmentId?.message}>
              {({ id, invalid, describedBy }) => (
                <Controller
                  control={control}
                  name="environmentId"
                  render={({ field }) => (
                    <EnvironmentSelect
                      id={id}
                      invalid={invalid}
                      describedBy={describedBy}
                      projectCode={projectCode}
                      value={field.value ?? null}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </Field>
            <Field label={t('common.layer')} error={errors.layerId?.message}>
              {({ id, invalid, describedBy }) => (
                <Controller
                  control={control}
                  name="layerId"
                  render={({ field }) => (
                    <CaseAttributeSelect
                      id={id}
                      invalid={invalid}
                      describedBy={describedBy}
                      projectCode={projectCode}
                      kind="layer"
                      value={field.value ?? null}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </Field>
            <Field label={t('common.module')} error={errors.moduleId?.message}>
              {({ id, invalid, describedBy }) => (
                <Controller
                  control={control}
                  name="moduleId"
                  render={({ field }) => (
                    <CaseAttributeSelect
                      id={id}
                      invalid={invalid}
                      describedBy={describedBy}
                      projectCode={projectCode}
                      kind="module"
                      value={field.value ?? null}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </Field>
            <Field label={t('enum.priorityLabel')} error={errors.priority?.message}>
              {({ id, describedBy }) => (
                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <SelectMenu
                      id={id}
                      aria-describedby={describedBy}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      {CASE_PRIORITIES.map((value) => (
                        <SelectMenuItem key={value} value={value}>
                          {priorityLabel(value)}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  )}
                />
              )}
            </Field>
            <Field label={t('enum.severityLabel')} error={errors.severity?.message}>
              {({ id, describedBy }) => (
                <Controller
                  control={control}
                  name="severity"
                  render={({ field }) => (
                    <SelectMenu
                      id={id}
                      aria-describedby={describedBy}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      {CASE_SEVERITIES.map((value) => (
                        <SelectMenuItem key={value} value={value}>
                          {severityLabel(value)}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  )}
                />
              )}
            </Field>
            <Field label={t('common.status')} error={errors.status?.message}>
              {({ id, describedBy }) => (
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SelectMenu
                      id={id}
                      aria-describedby={describedBy}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      {CASE_STATUSES.map((value) => (
                        <SelectMenuItem key={value} value={value}>
                          {caseStatus(value)}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  )}
                />
              )}
            </Field>
            <Field label={t('cases.automated')} error={errors.isAutomated?.message}>
              {({ id, describedBy }) => (
                <Controller
                  control={control}
                  name="isAutomated"
                  render={({ field }) => (
                    <SelectMenu
                      id={id}
                      aria-describedby={describedBy}
                      value={field.value ? 'yes' : 'no'}
                      onValueChange={(value) => field.onChange(value === 'yes')}
                    >
                      <SelectMenuItem value="yes">{automation(true)}</SelectMenuItem>
                      <SelectMenuItem value="no">{automation(false)}</SelectMenuItem>
                    </SelectMenu>
                  )}
                />
              )}
            </Field>
            <Field label={t('labels.title')}>
              {({ id }) => (
                <LabelPicker
                  id={id}
                  projectCode={projectCode}
                  value={labelIds}
                  onChange={setLabelIds}
                />
              )}
            </Field>
          </div>

          <Field label={t('common.description')} error={errors.description?.message}>
            {({ id, invalid, describedBy }) => (
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <RichTextEditor
                    id={id}
                    invalid={invalid}
                    aria-describedby={describedBy}
                    projectCode={projectCode}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    rows={5}
                  />
                )}
              />
            )}
          </Field>

          <Field
            label={t('cases.preconditions')}
            error={errors.preconditions?.message}
            hint={t('cases.preconditionsHint')}
          >
            {({ id, invalid, describedBy }) => (
              <Controller
                control={control}
                name="preconditions"
                render={({ field }) => (
                  <RichTextEditor
                    id={id}
                    invalid={invalid}
                    aria-describedby={describedBy}
                    projectCode={projectCode}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    rows={3}
                  />
                )}
              />
            )}
          </Field>

          {/* Test data describes the whole scenario, so it sits with the
              preconditions rather than inside every step. */}
          <Field
            label={t('cases.stepData')}
            error={errors.testData?.message}
            hint={t('cases.testDataHint')}
          >
            {({ id, invalid, describedBy }) => (
              <Controller
                control={control}
                name="testData"
                render={({ field }) => (
                  <RichTextEditor
                    id={id}
                    invalid={invalid}
                    aria-describedby={describedBy}
                    projectCode={projectCode}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    rows={3}
                  />
                )}
              />
            )}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('cases.steps')} description={t('cases.stepsHint')} />
        <CardBody>
          <StepsEditor
            projectCode={projectCode}
            steps={steps}
            errors={errors}
            onChange={setSteps}
          />
        </CardBody>
      </Card>
    </form>
  );
};
